import cs from 'classnames';
import moment from 'moment';
import { Divider, Space, Typography } from 'antd';
import { AiOutlineWhatsApp } from 'react-icons/ai';
import { Trans, useTranslation } from 'react-i18next';
import { formatPhone } from '../../../utils/bookings';
import { IBooking, IBookingEvent } from '../../../interfaces/booking.interface';
import ItemData from '../Item';

interface Props {
    showDetail?: boolean;
    type?: 'SUCCESS' | 'FAILED' | 'RESOLVING'
    booking: IBooking;
    bookingEvent?: IBookingEvent;
    bookingEventSelected: boolean;
}

const { Text } = Typography;

const CreateStatus = ({
    showDetail = false, type = 'SUCCESS', booking, bookingEvent, bookingEventSelected,
}: Props) => {
    const { t } = useTranslation('bookings', { keyPrefix: 'DETAIL' });
    const { t: ti } = useTranslation('bookings', { keyPrefix: 'ORDER_FINISHED' });

    const phoneFormatted = booking?.customerMobile ? formatPhone(booking?.customerMobile, booking?.deliveryProvider) : null;

    const date = moment(bookingEvent?.createdAt).format('DD MMM');
    const time = moment(bookingEvent?.createdAt).format('HH:mm');

    return (
        !showDetail ? (
            <div className={cs('booking__status', { active: bookingEventSelected })}>
                <Text>
                    <Trans
                        t={t}
                        i18nKey="STORY_LINE.STATUS.BOOKING_CREATED"
                        components={{ b: <b className="booking__status--bold" /> }}
                        values={{
                            bookingNumericId: booking.bookingNumericId,
                        }}
                    />
                </Text>
                <Text className="booking__status--time">
                    {moment(bookingEvent?.createdAt).format('DD MMM HH:mm')}
                </Text>
            </div>
        ) : (
            <div className="booking__status--detail">
                <Space direction="vertical" size={4}>
                    <Text className="booking__status--detail-title">
                        {t('STORY_LINE.EVENT_DETAILS')}
                    </Text>

                    <Text className={`booking__status--detail-tag ${type}`}>
                        {t(`STORY_LINE.${type}`)}
                    </Text>
                </Space>

                <Text>
                    {
                        bookingEvent?.origin === 'MANUAL'
                            ? (
                                <Trans
                                    t={t}
                                    i18nKey="STORY_LINE.STATUS.BOOKING_CREATED_BY"
                                    components={{ b: <b className="booking__status--bold" /> }}
                                    values={{
                                        userName: bookingEvent?.registeredBy?.name || booking?.userBookingCreator || '',
                                        bookingNumericId: booking.bookingNumericId,
                                        origin: t(`STORY_LINE.BOOKING_ORIGIN.${booking.origin}`),
                                        date,
                                        time,
                                    }}
                                />
                            )
                            : (
                                <Trans
                                    t={t}
                                    i18nKey="STORY_LINE.STATUS.BOOKING_CREATED_DESCRIPTION"
                                    components={{ b: <b className="booking__status--bold" /> }}
                                    values={{
                                        bookingNumericId: booking.bookingNumericId,
                                        origin: t(`STORY_LINE.BOOKING_ORIGIN.${booking.origin}`),
                                        date,
                                        time,
                                    }}
                                />
                            )
                    }
                </Text>

                <Divider />

                <Text className="booking__status--detail-subtitle">
                    {t('STORY_LINE.INFORMATION')}
                </Text>

                <div className="booking__status--detail-info">
                    <ItemData
                        label={t('BUSINESS_NAME')}
                        value={booking.businessName || t('NOT_AVAILABLE')}
                    />
                    <ItemData
                        label={t('CUSTOMER')}
                        value={booking.customerName}
                    />
                    <ItemData
                        label={t('CUSTOMER_MOBILE')}
                        value={phoneFormatted}
                        actionRequired={phoneFormatted !== null}
                        actionIcon={(
                            <AiOutlineWhatsApp
                                color="#6C8EEF"
                                size={16}
                                style={{ verticalAlign: 'middle' }}
                            />
                        )}
                        action={
                            phoneFormatted !== null
                                ? () => window.open(`https://wa.me/${phoneFormatted}`, '_blank')
                                : () => {}
                        }
                    />

                    <ItemData
                        label={t('LOCATIONS.PICKUP')}
                        value={(
                            <Space size={2} direction="vertical">
                                {booking?.pickupAddress}
                                {
                                    (!booking?.pickupReferences || booking?.pickupReferences === 'N/A')
                                        ? t('LOCATIONS.NO_REFERENCE')
                                        : booking?.pickupReferences
                                }
                            </Space>
                        )}
                    />

                    <ItemData
                        label={t('LOCATIONS.DELIVER')}
                        value={(
                            <Space size={2} direction="vertical">
                                {booking?.deliveryAddress}
                                {
                                    (!booking?.deliveryReferences || booking?.deliveryReferences === 'N/A')
                                        ? t('LOCATIONS.NO_REFERENCE')
                                        : booking?.deliveryReferences
                                }
                            </Space>
                        )}
                    />

                    <ItemData
                        label={ti('PAYMENT_METHOD')}
                        value={ti(booking?.paymentMethod)}
                    />

                    <ItemData
                        label={t('LOCATIONS.DISTANCE_COVERED')}
                        value={`${booking?.distanceBreakUp.exactDistance?.toFixed(2)} Km`}
                    />

                    <ItemData
                        label={t('EXTERNAL.NOTES')}
                        value={booking?.bookingNotes || t('EXTERNAL.EMPTY_NOTES')}
                    />
                </div>
            </div>
        )
    );
};

export default CreateStatus;
